import { attachmentPatch } from "../attachments/attachmentState";

// TGlobal Product Assistant — Root Agent (`TGII_PIMS_RootAgent` repo), deployed to
// Cloud Run as `tg-root-agent-service`. Same ADK `/run_sse` protocol as the FAQ
// agent, so the transport below is a simplified copy of Faq/api/chat.js.
// Spec: Ref/root_agent/tg_pims_assistant_frontend_spec_2026_08_27.md
export const ROOT_AGENT_API =
  process.env.REACT_APP_ROOT_AGENT_URL ||
  "https://tg-root-agent-service-1050393382437.us-central1.run.app";

// ADK derives the app name from the agent directory (`services/root_agent/`), so
// this is "root_agent", NOT the FAQ service's "agent". Verified against the live
// endpoint — see spec §2.2.
const APP_NAME = "root_agent";

// Root Agent emits its own semantic stage values in stateDelta (camelCase — the
// prose in the backend's docs/sse_contract.md says `state_delta`, but the real
// JSON is `stateDelta`, confirmed against the deployed service). The value set is
// Root's own, NOT the FAQ agent's understanding/searching/comparing. See spec §3.
// Reference only — nothing validates against this, and the UI falls back to a
// generic label for anything it does not recognise. `searching`/`synthesizing`
// arrive on the FAQ path, `web_searching` right before a GPT call.
export const STAGES = [
  "reading_attachments",
  "routing",
  "querying",
  "searching",
  "synthesizing",
  "web_searching",
  "composing",
  "error",
];

function randomId(prefix) {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`;
}

const authHeaders = (token) => ({ Authorization: `Bearer ${token}` });

// sessionId: pass one to reopen a conversation continued from History under its
// own id, so the agent's session and the saved conversation keep sharing it. ADK
// refuses to create a session that already exists; that is the agent still
// having it, which is what the caller wants, so it counts as success.
export async function createChatSession(token, userId, sessionId) {
  const id = sessionId || randomId(process.env.REACT_APP_ROOT_AGENT_SESSION_PREFIX || "session");
  const response = await fetch(
    `${ROOT_AGENT_API}/apps/${APP_NAME}/users/${userId}/sessions/${id}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders(token) },
      body: JSON.stringify({}),
    },
  );
  const alreadyExists =
    sessionId && (response.status === 400 || response.status === 409);
  if (!response.ok && !alreadyExists) {
    throw new Error(`Failed to create chat session: ${response.status}`);
  }
  return id;
}

/**
 * Stream one turn from the Root Agent.
 *
 * Event shape (spec §3): a few `stateDelta.stage` events, then either ONE content
 * event (`partial: false`) or TWO (a complete table with `partial: true`, then the
 * LLM analysis with `partial: false`). `partial: true` here does NOT mean "a chunk
 * to concatenate character by character" — each content event is a complete block
 * of text; `partial: true` only means "more content events follow in this turn".
 *
 * There is NO `turn_complete` event — the end of the turn is the SSE stream
 * closing (`reader.read()` → `done: true`). Waiting on a field would hang forever.
 * That also covers the degraded case where the analysis LLM call fails and only the
 * `partial: true` table arrives: the stream just closes and whatever blocks we
 * collected are the final answer.
 *
 * handlers: { onStageChange, onBlock, onSources, onDone, onError }
 *   onBlock(text) — one complete text block; the caller appends it to the message
 *                   separated by a blank line (spec §4: both blocks must be parsed
 *                   together by MessageRenderer, not separately).
 *   onSources(sources) — FAQ-path answers only: the [1], [2]... citation list from
 *                   the terminal event's stateDelta.sources (faq_flow.py). Optional
 *                   handler; product_direct answers never call it.
 *   onVideos(videos) — video answers only: structured cards (title, url, summary,
 *                   chapters with timestamps) for the same videos the markdown
 *                   text lists. Optional handler.
 *   onDone({ blocks }) — the stream closed; the turn is over.
 *
 * includeVideo — answer with videos when the library has a match. On a miss the
 *   backend sends a short "no video, falling back to text" block first and the
 *   text answer second, which onBlock already renders in order.
 *
 * { model, thinking, verbosity } — answer settings, applied to both the product
 *   and FAQ paths. Always pass concrete values: JSON.stringify drops undefined
 *   keys, and stateDelta merges, so an omitted setting silently keeps whatever
 *   the previous turn used instead of returning to the default.
 */
export async function streamChatMessage(
  token,
  userId,
  sessionId,
  message,
  handlers,
  language,
  includeVideo,
  { model, thinking, verbosity, attachmentIds = [], fetchImpl = fetch, signal } = {},
) {
  let response;
  try {
    response = await fetchImpl(`${ROOT_AGENT_API}/run_sse`, {
      method: "POST",
      ...(signal ? { signal } : {}),
      headers: { "Content-Type": "application/json", ...authHeaders(token) },
      body: JSON.stringify({
        appName: APP_NAME,
        userId,
        sessionId,
        newMessage: { role: "user", parts: [{ text: message }] },
        streaming: true,
        ...(attachmentIds.length > 0
          ? { customMetadata: { attachments: { version: "assistant-v1", ids: [...attachmentIds] } } }
          : {}),
        // The toggle wins over what the question is written in: the user can ask
        // in English and want the answer in Chinese, which is how CX behaved and
        // what script detection alone cannot express. The backend normalizes the
        // code ("zh" -> "tw") and falls back to detecting the question's script
        // when this is absent, so omitting it stays safe.
        //
        // Sent per request rather than at session creation, because the toggle can
        // change mid-conversation and /run_sse merges stateDelta into the session
        // before the agent runs.
        //
        // Both flags share ONE stateDelta object. Spreading two separate
        // `{ stateDelta: ... }` objects would make the later key overwrite the
        // earlier one, silently dropping user_lang whenever video is on.
        //
        // `include_video` is snake_case to match what the backend reads; a typo
        // here fails silently rather than erroring.
        //
        // Sent as an explicit false when off, never omitted: stateDelta MERGES
        // into the session, so a key that is simply left out keeps whatever the
        // previous turn set. Omitting it made the toggle one-way — switching it
        // off still produced video answers for the rest of the conversation.
        // model / thinking / verbosity are also sent every turn, for the same
        // merge reason, and apply to both the product and FAQ paths — Root
        // forwards them to the FAQ service. The backend whitelists each value
        // and silently falls back to its default on anything unrecognised, so a
        // misspelled id produces no error, just a setting that does nothing;
        // the ids here have to match the backend's exactly.
        stateDelta: {
          ...(language ? { user_lang: language } : {}),
          include_video: includeVideo === true,
          model,
          thinking,
          verbosity,
        },
      }),
    });
  } catch (error) {
    handlers.onError(error instanceof Error ? error : new Error(String(error)));
    return;
  }

  // Keep the legacy text parser below unchanged. Strict failure handling belongs
  // only to an explicit attachment turn, never to ordinary degraded text replies.
  if (attachmentIds.length > 0) {
    await consumeRootStream(response, handlers);
    return;
  }

  if (!response.ok || !response.body) {
    handlers.onError(new Error(`Agent request failed: ${response.status}`));
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  const blocks = [];

  let stage = null;
  function setStage(next) {
    if (stage === next) return;
    stage = next;
    handlers.onStageChange(next);
  }

  function handleEvent(event) {
    const delta = event.actions?.stateDelta;
    const patch = attachmentPatch(delta || {});
    if (Object.keys(patch).length > 0) handlers.onAttachment?.(patch);
    const nextStage = delta?.stage;
    if (nextStage) setStage(nextStage);

    // FAQ answers cite sources inline as [1], [2]... — the terminal event's
    // stateDelta carries the source list those numbers refer to (see
    // faq_flow.py's final yield). Not present for product_direct answers.
    // The raw objects only have {title, domain, url, image, description,
    // enrichment_status} (web_search.py) — no index; array position IS the
    // citation number, same convention FAQ's own frontend uses (toWebSources
    // in Faq/api/chat.js).
    if (Array.isArray(delta?.sources) && delta.sources.length > 0) {
      handlers.onSources?.(
        delta.sources.map((s, i) => ({ ...s, index: i + 1 })),
      );
    }

    // Video answers carry a structured card list alongside the markdown text —
    // same list, same order, already deduped and capped at 3 by the backend. The
    // key is absent entirely when the library had no match, in which case the
    // text answer is the whole reply.
    if (Array.isArray(delta?.videos) && delta.videos.length > 0) {
      handlers.onVideos?.(delta.videos);
    }

    // Who actually answered this turn. "gpt" means the backend judged the
    // question complex and searched tglobalcorp.com live instead of querying our
    // database — which it does even when the picker is left on Gemini, so the
    // answer's provenance cannot be inferred from the selected model. It also
    // takes 1-2 minutes, so the wait needs a different loading message.
    const worker = event.output?.worker;
    if (worker) handlers.onWorker?.(worker);

    // A content event carries either a whole block of answer text or one
    // streamed fragment of a sentence — the video path's AI blurb arrives as a
    // run of events with no line breaks of their own. The caller decides how to
    // join them; see appendBlock in TgProductAssistant.js.
    const parts = event.content?.parts;
    if (!Array.isArray(parts)) return;
    const text = parts.map((p) => p.text ?? "").join("");
    if (!text) return;
    blocks.push(text);
    handlers.onBlock(text);
  }

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        if (!line.startsWith("data:")) continue;
        const payload = line.slice("data:".length).trim();
        if (!payload) continue;

        let event;
        try {
          event = JSON.parse(payload);
        } catch {
          continue;
        }
        handleEvent(event);
      }
    }

    // Stream closed = turn complete (spec §3.1). Anything still buffered without a
    // trailing newline is a final `data:` line the loop above never saw.
    const tail = buffer.trim();
    if (tail.startsWith("data:")) {
      try {
        handleEvent(JSON.parse(tail.slice("data:".length).trim()));
      } catch {
        /* ignore a truncated trailing line */
      }
    }

    handlers.onDone({ blocks });
  } catch (error) {
    handlers.onError(error instanceof Error ? error : new Error(String(error)));
  }
}

export function buildTurnRequest(userId, sessionId, message, language, includeVideo,
  { model, thinking, verbosity, attachmentIds = [] } = {}) {
  return { appName: APP_NAME, userId, sessionId,
    newMessage: { role: "user", parts: [{ text: message }] }, streaming: true,
    ...(attachmentIds.length ? { customMetadata: { attachments: { version: "assistant-v1", ids: [...attachmentIds] } } } : {}),
    stateDelta: { ...(language ? { user_lang: language } : {}), include_video: includeVideo === true, model, thinking, verbosity } };
}

// Attachment turns only: streamChatMessage gates this parser on nonempty attachmentIds.
// EOF cannot erase an attachment error or make an empty attachment answer successful.
export async function consumeRootStream(response, handlers) {
  if (!response.ok || !response.body) {
    let detail;
    try { detail = (await response.json()).error; } catch { /* non-JSON transport failure */ }
    handlers.onError(Object.assign(new Error(detail?.message || `Agent request failed: ${response.status}`), { detail }));
    return;
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const blocks = [];
  let buffer = "", data = [], failed = false;
  function fail(error) { if (!failed) { failed = true; handlers.onError(error); } }
  function event(payload) {
    let value;
    try { value = JSON.parse(payload); }
    catch { fail(new Error("串流資料不完整，請重試。")); return; }
    const delta = value.actions?.stateDelta || {};
    handlers.onAttachment?.(attachmentPatch(delta));
    if (delta.stage) handlers.onStageChange?.(delta.stage);
    if (delta.attachment_error || delta.stage === "error" || value.error) {
      const detail = delta.attachment_error || value.error;
      fail(Object.assign(new Error(detail?.message || "服務回報錯誤。"), { detail }));
    }
    if (Array.isArray(delta.sources)) handlers.onSources?.(delta.sources.map((source, i) => ({ ...source, index: i + 1 })));
    if (Array.isArray(delta.videos)) handlers.onVideos?.(delta.videos);
    if (value.output?.worker) handlers.onWorker?.(value.output.worker);
    const text = value.content?.parts?.map(part => part.text || "").join("");
    if (text) { blocks.push(text); handlers.onBlock?.(text); }
  }
  function line(text) {
    if (!text) { if (data.length) event(data.join("\n")); data = []; }
    else if (text.startsWith("data:")) data.push(text.slice(5).trimStart());
  }
  try {
    while (true) {
      const result = await reader.read();
      if (result.done) break;
      buffer += decoder.decode(result.value, { stream: true });
      let newline;
      while ((newline = buffer.indexOf("\n")) >= 0) {
        line(buffer.slice(0, newline).replace(/\r$/, "")); buffer = buffer.slice(newline + 1);
      }
    }
    buffer += decoder.decode();
    if (buffer.trim()) line(buffer.replace(/\r$/, ""));
    line("");
    if (!blocks.length) fail(new Error("串流已中斷或未收到回答，請重試。"));
    if (!failed) handlers.onDone?.({ blocks });
  } catch (error) { fail(error instanceof Error ? error : new Error(String(error))); }
  finally { reader.releaseLock?.(); }
}
